import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import type { BarcodePrintDto, BarcodeUnitDto } from "@delta/shared";
import { useLang } from "../../i18n";
import { api } from "../../lib/api";
import { errorText } from "../../lib/errors";
import { Icon } from "../../components/Icon";
import { PageHead, Shell } from "../../components/Shell";
import { ListView, type Column } from "../../components/ListView";
import { useToast } from "../../components/Toast";
import { amt } from "../ledger/common";
import { Barcode } from "./common";
import { agentCall, savedToken } from "../../lib/agent";
import { roleMapping, type AgentJob } from "../../print/direct";
import { labelDoc } from "../../print/documents";
import { loadPrintFonts, renderDoc } from "../../print/render";
import { useSession } from "../../lib/session";

// Items › Barcode print, ported from the prototype (erp-items.js "/items/qrPrint"): pick item units, the copies of
// each, and print a sheet of labels (organization, item, unit, Code 128 barcode, code and retail price) through the
// browser's print dialog. Delta sends nothing to a label printer and makes no claim about label printer support.

type Row = BarcodeUnitDto & { id: string };

/** Sends the labels to this PC's label printer through the POS agent (phase 12a), one page per label. */
function useDirectLabels(labels: Row[], org: string) {
  const { t, L, lang } = useLang();
  const s = useSession();
  const [ready, setReady] = useState<boolean | null>(null);
  const [state, setState] = useState<string | null>(null);
  useEffect(() => { void roleMapping("label").then((m) => setReady(m.ok)); }, []);
  const print = async () => {
    const m = await roleMapping("label");
    if (!m.ok) { setState(t(`printReason_${m.reason}`)); return; }
    setState(t("printWorking"));
    await loadPrintFonts();
    const pages = labels.flatMap((r) => renderDoc(labelDoc({ org, nameAr: r.item.nameAr, nameEn: r.item.nameEn, unitAr: r.unit.nameAr, unitEn: r.unit.nameEn, barcode: r.barcode, price: r.price }, { t, L, lang }),
      { widthMm: m.mapping.paper.widthMm, heightMm: m.mapping.paper.heightMm, dpi: m.mapping.paper.dpi, marginMm: 1.5 }).slice(0, 1));
    const jobId = crypto.randomUUID();
    const r = await agentCall<AgentJob>("POST", "/print/jobs", { jobId, role: "label", documentRef: `labels:${jobId}`, copy: false, title: `Labels ×${labels.length}`, pages, user: s.me!.user.name }, savedToken(), 60_000);
    if (!r.body) { setState(t("printFailed", { reason: t(`printReason_${r.code ?? "agent_unreachable"}`) })); return; }
    for (let i = 0; i < 240; i++) {
      const j = await agentCall<AgentJob>("GET", `/print/jobs/${jobId}`);
      const st = j.body?.state;
      if (st === "done") { setState(t("printDone", { printer: j.body!.printer ?? "" })); return; }
      if (st === "failed" || st === "cancelled") { setState(t("printFailed", { reason: t(`printReason_${j.body!.error ?? st}`) })); return; }
      if (st === "unknown") { setState(t("printUnknown", { reason: t(`printReason_${j.body!.error ?? "unknown"}`) })); return; }
      await new Promise((res) => setTimeout(res, 500));
    }
  };
  return { ready, state, print };
}

function LabelSheet({ labels, org, onClose }: { labels: Row[]; org: string; onClose(): void }) {
  const { t } = useLang();
  const direct = useDirectLabels(labels, org);
  useEffect(() => { document.body.classList.add("pv-open"); return () => document.body.classList.remove("pv-open"); }, []);
  return createPortal(
    <div className="pv-back" role="dialog" aria-modal="true" aria-label={t("barcodePrint")} data-testid="label-sheet">
      <div className="pv-bar"><b>{t("barcodePrint")} · <span className="num">{labels.length}</span></b><span className="spacer" style={{ flex: 1 }} />
        {direct.state && <span className="muted" role="status" data-testid="label-direct-status">{direct.state}</span>}
        {direct.ready && <button className="btn btn-primary" onClick={() => void direct.print()} data-testid="label-direct"><Icon name="printer" />{t("printOnLabelPrinter")}</button>}
        <button className={direct.ready ? "btn" : "btn btn-primary"} onClick={() => window.print()} data-testid="label-print"><Icon name="printer" />{direct.ready ? t("printBrowser") : t("print")}</button>
        <button className="btn" onClick={onClose}>{t("close")}</button></div>
      <div className="pv-scroll"><div className="pv-page">
        <div className="bl-sheet">{labels.map((r, i) => (
          <div key={`${r.id}-${i}`} className="bl" data-testid="label">
            <div className="bl-org">{org}</div><div className="bl-name">{r.item.nameEn === r.item.nameAr ? r.item.nameEn : <><span lang="ar">{r.item.nameAr}</span> · <span lang="en">{r.item.nameEn}</span></>}</div>
            <div className="bl-unit">{r.unit.nameAr} · {r.unit.nameEn}</div>
            <Barcode text={r.barcode} h={34} /><div className="bl-code num">{r.barcode}</div><div className="bl-price num">{amt(r.price)} {t("currency")}</div>
          </div>
        ))}</div>
        <p className="hint pv-hint">{t("labelHintDelta")}</p>
      </div></div>
    </div>, document.body);
}

export function BarcodePrint() {
  const { t, L } = useLang();
  const toast = useToast();
  const [data, setData] = useState<BarcodePrintDto | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [sel, setSel] = useState<Record<string, boolean>>({});
  const [copies, setCopies] = useState<Record<string, string>>({});
  const [sheet, setSheet] = useState<Row[] | null>(null);
  const load = useCallback(async () => {
    setFailed(null);
    const p = new URLSearchParams();
    if (filters.name) p.set("q", filters.name);
    if (filters.cat) p.set("categoryId", filters.cat);
    if (filters.bc) p.set("barcode", filters.bc);
    try { setData(await api<BarcodePrintDto>("GET", `/items/barcode-print${p.size ? "?" + p : ""}`)); } catch (e) { setFailed(errorText(t, e)); }
  }, [filters.name, filters.cat, filters.bc, t]);
  useEffect(() => { void load(); }, [load]);
  const rows: Row[] | null = data ? data.units.map((u) => ({ ...u, id: u.itemUnitId })) : null;
  const printable = (rows ?? []).filter((r) => r.barcode);
  const nSel = printable.filter((r) => sel[r.id]).length;
  const count = (id: string) => Math.max(1, Math.min(500, Math.round(Number(copies[id]) || 1)));
  function print() {
    const list = printable.filter((r) => sel[r.id]);
    if (!list.length) { toast(t("selectToPrint")); return; }
    setSheet(list.flatMap((r) => Array.from({ length: count(r.id) }, () => r)));
  }
  const allOn = printable.length > 0 && printable.every((r) => sel[r.id]);
  const columns: Column<Row>[] = [
    { k: "sel", label: t("selectRow"), cell: (r) => <input type="checkbox" checked={!!sel[r.id]} disabled={!r.barcode} onChange={(e) => setSel((x) => ({ ...x, [r.id]: e.target.checked }))} aria-label={t("selectRow")} data-testid="bp-sel" />, csv: () => "" },
    { k: "ar", label: t("nameAr"), cell: (r) => <span lang="ar">{r.item.nameAr}</span>, csv: (r) => r.item.nameAr },
    { k: "en", label: t("nameEn"), cell: (r) => <span lang="en">{r.item.nameEn}</span>, csv: (r) => r.item.nameEn },
    { k: "cat", label: t("category"), cell: (r) => L(r.category), csv: (r) => L(r.category) },
    { k: "unit", label: t("unit"), cell: (r) => L(r.unit), csv: (r) => L(r.unit) },
    { k: "code", label: t("unitBarcode"), cell: (r) => (r.barcode ? <span className="num" dir="ltr">{r.barcode}</span> : <span className="muted">{t("noBarcode")}</span>), csv: (r) => r.barcode },
    { k: "price", label: t("price"), end: true, cell: (r) => amt(r.price), csv: (r) => amt(r.price) },
    { k: "copies", label: t("copiesNo"), cell: (r) => <input className="control num w-copies" type="number" min={1} max={500} value={copies[r.id] ?? "1"} disabled={!r.barcode}
      onChange={(e) => setCopies((x) => ({ ...x, [r.id]: e.target.value }))} aria-label={t("copiesNo")} data-testid="bp-copies" style={{ width: 80 }} />, csv: (r) => count(r.id) }
  ];
  const tools = <>
    <label className="check-row hint"><input type="checkbox" checked={allOn} onChange={(e) => setSel(Object.fromEntries(printable.map((r) => [r.id, e.target.checked])))} data-testid="bp-all" /> {t("selectAll")}</label>
    <span className="hint" data-testid="bp-count">{t("selectedN", { n: nSel })}</span>
    <button className="btn btn-primary" disabled={!nSel} onClick={print} data-testid="bp-print"><Icon name="printer" />{t("print")}</button>
  </>;
  return (
    <Shell navKey="/items/qrPrint">
      <PageHead crumbs={[[t("home"), "/home"], [t("items")], [t("barcodePrint")]]} title={t("barcodePrint")} />
      <ListView rows={rows} error={failed} columns={columns} file="barcodes" testId="barcode-print-table" onRefresh={() => void load()}
        search={[
          { k: "cat", label: t("categoryName"), type: "select", options: (data?.categories ?? []).map((c) => [c.id, L(c)]) },
          { k: "name", label: t("itemName"), type: "text" },
          { k: "bc", label: t("unitBarcodes"), type: "text" }
        ]} filters={filters} onFilters={setFilters}
        quick={(r) => `${r.item.nameEn} ${r.item.nameAr} ${r.barcodes.join(" ")}`} tools={tools}
        empty={{ icon: "box", title: t("noResults"), body: t("noResultsBody") }}
        actions={(r) => [r.barcode ? { label: t("print"), icon: "printer", run: () => setSheet(Array.from({ length: count(r.id) }, () => r)) }
          : { label: t("noBarcode"), icon: "printer", run: () => toast(t("selectToPrint")), disabled: true }]} />
      {sheet && <LabelSheet labels={sheet} org={data ? L(data.organization) : ""} onClose={() => setSheet(null)} />}
    </Shell>
  );
}
